import { useState } from "react";
import { ArrowUp, ArrowDown, X, Plus, Check } from "lucide-react";

export const RULE_TYPES = {
  boarded_between: { label: "Boarded between", fields: ["start", "end"] },
  not_booked_between: { label: "Not booked between", fields: ["start", "end"] },
  visited_within_days: { label: "Visited in the last N days", fields: ["days"] },
  last_visit_older_than: { label: "Last visit N+ days ago", fields: ["days"] },
  min_dogs: { label: "N+ active dogs", fields: ["n"] },
  ever_boarded: { label: "Has boarded before", fields: [] },
  never_boarded: { label: "Daycare only, never boarded", fields: [] },
  min_visits: { label: "N+ visits of a service", fields: ["service", "n"] },
  in_area: { label: "Lives in towns / zips", fields: ["places"] },
};

const fmt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "?");
const SVC = { boarding: "boarding stays", daycare: "daycare visits", any: "visits" };
export const ruleLabel = (r) => ({
  boarded_between: () => `Boarded ${fmt(r.start)}–${fmt(r.end)}`,
  not_booked_between: () => `Not booked for ${fmt(r.start)}–${fmt(r.end)}`,
  visited_within_days: () => `Visited in the last ${r.days} days`,
  last_visit_older_than: () => `Last visit ${r.days}+ days ago`,
  min_dogs: () => `${r.n}+ active dogs`,
  ever_boarded: () => "Has boarded before",
  never_boarded: () => "Daycare only, never boarded",
  min_visits: () => `${r.n}+ ${SVC[r.service] || "visits"}`,
  in_area: () => `Lives in ${(r.places || []).join(", ")}`,
}[r.type] || (() => r.type))();

const RuleEditor = ({ rule, onSave, onCancel }) => {
  const [r, setR] = useState(rule);
  const f = RULE_TYPES[r.type].fields;
  return (
    <div className="raised" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", padding: 8, borderRadius: 8 }} data-testid="rule-editor">
      <select className="input" value={r.type} onChange={(e) => setR({ type: e.target.value })} data-testid="rule-type">
        {Object.entries(RULE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      {f.includes("start") && <input type="date" className="input" value={r.start || ""} onChange={(e) => setR({ ...r, start: e.target.value })} data-testid="rule-start" />}
      {f.includes("end") && <input type="date" className="input" value={r.end || ""} onChange={(e) => setR({ ...r, end: e.target.value })} data-testid="rule-end" />}
      {f.includes("days") && <input type="number" min={1} className="input" style={{ width: 90 }} value={r.days || ""} onChange={(e) => setR({ ...r, days: Number(e.target.value) })} placeholder="days" data-testid="rule-days" />}
      {f.includes("service") && (
        <select className="input" value={r.service || "any"} onChange={(e) => setR({ ...r, service: e.target.value })} data-testid="rule-service">
          <option value="boarding">Boarding stays</option><option value="daycare">Daycare visits</option><option value="any">Any visits</option>
        </select>
      )}
      {f.includes("n") && <input type="number" min={1} className="input" style={{ width: 70 }} value={r.n || ""} onChange={(e) => setR({ ...r, n: Number(e.target.value) })} placeholder={r.type === "min_dogs" ? "dogs" : "visits"} data-testid="rule-n" />}
      {f.includes("places") && <input className="input" style={{ minWidth: 220 }} value={(r.places || []).join(", ")} onChange={(e) => setR({ ...r, places: e.target.value.split(",").map((p) => p.trimStart()) })} placeholder="Chalfont, 18914" data-testid="rule-places" />}
      <button className="btn btn-sm btn-primary" onClick={() => onSave({ ...r, ...(r.places ? { places: r.places.map((p) => p.trim()).filter(Boolean) } : {}) })} data-testid="rule-save"><Check size={12} />Apply</button>
      <button className="btn btn-sm" onClick={onCancel} data-testid="rule-cancel">Cancel</button>
    </div>
  );
};

export const RuleChips = ({ rules, labels, onChange, busy }) => {
  const [edit, setEdit] = useState(null);
  const save = (r) => { const next = edit === "new" ? [...rules, r] : rules.map((x, i) => (i === edit ? r : x)); setEdit(null); onChange(next); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="rule-chips">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {rules.map((r, i) => (
          <span key={i} className="chip" style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }} data-testid={`rule-chip-${i}`}>
            <span onClick={() => !busy && setEdit(i)}>{(labels && labels[i]) || ruleLabel(r)}</span>
            <button className="btn btn-ghost btn-sm" style={{ padding: 0, minHeight: 0 }} disabled={busy} onClick={() => onChange(rules.filter((_, j) => j !== i))} aria-label="Remove rule" data-testid={`rule-remove-${i}`}><X size={11} /></button>
          </span>
        ))}
        <button className="chip" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} disabled={busy} onClick={() => setEdit("new")} data-testid="rule-add"><Plus size={11} />Add rule</button>
      </div>
      {edit !== null && <RuleEditor key={String(edit)} rule={edit === "new" ? { type: "visited_within_days", days: 365 } : rules[edit]} onSave={save} onCancel={() => setEdit(null)} />}
    </div>
  );
};

export const PriorityList = ({ order, groups, onChange, busy }) => {
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; onChange(o); };
  const removed = Object.keys(groups).filter((k) => !order.includes(k));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="priority-list">
      {order.map((k, i) => (
        <div key={k} className="raised" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8 }} data-testid={`priority-${k}`}>
          <span className="mono" style={{ fontWeight: 700, color: "var(--accent-ink)", width: 16 }}>{i + 1}</span>
          <div style={{ flex: 1 }}><b style={{ fontSize: 13 }}>{groups[k][0]}</b><div className="muted" style={{ fontSize: 11 }}>{groups[k][1]}</div></div>
          <button className="btn btn-sm" disabled={busy || !i} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`priority-up-${k}`}><ArrowUp size={14} /></button>
          <button className="btn btn-sm" disabled={busy || i === order.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`priority-down-${k}`}><ArrowDown size={14} /></button>
          <button className="btn btn-sm" title="Leave this group out" disabled={busy || order.length === 1} onClick={() => onChange(order.filter((o) => o !== k))} data-testid={`priority-remove-${k}`}><X size={14} /></button>
        </div>
      ))}
      {removed.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }} data-testid="priority-removed">
          <span className="muted" style={{ fontSize: 11 }}>Left out:</span>
          {removed.map((k) => (
            <button key={k} className="chip" disabled={busy} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }} title="Add back" onClick={() => onChange([...order, k])} data-testid={`priority-restore-${k}`}>
              <span style={{ textDecoration: "line-through" }}>{groups[k][0]}</span><Plus size={11} style={{ marginLeft: 4 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
